import { cn, NoddiIcon, type IconProps } from 'noddi-ui-common';
import { VisuallyHidden as VisuallyHiddenPrimitive } from 'radix-ui';
import React, { useEffect, useRef, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '../../base/sheet';
import { NoddiCircularLoader } from '../Feedback/Loaders/NoddiCircularLoader';
import { OverlayActionsSlot } from './OverlayActionsSlot';
import { onOverlayOpenAutoFocus } from './onOverlayOpenAutoFocus';
import { useSwipeToClose } from './useSwipeToClose';

interface BaseNoddiSheetProps {
  /** The sheet title — plain string, or a node for a richer header (e.g. avatar + name). */
  title?: React.ReactNode;
  /** The sheet description */
  description?: string;
  /**
   * Which side the sheet opens from.
   *
   * `'bottom'` is pinned to the bottom edge with an auto height, so it grows *upward*: any body
   * content that appears later (a suggestion list, an expanding section, an inline error) lifts
   * everything above it, and a field the customer is typing into can end up off the top of the
   * screen — worse with the phone keyboard already eating the lower half. Pin a height through
   * `className` (`h-[85dvh]` rather than `max-h-[85dvh]`) whenever the body can grow while a field
   * is focused; the body is a flex child with `overflow-auto`, so the new content takes scroll
   * space instead of resizing the sheet. Use `dvh`, never `vh`: iOS Safari's `vh` is the height
   * with its toolbars hidden, so a `vh` sheet runs under the address bar and its close button
   * leaves the screen. The base sheet already caps its height at `90dvh`.
   *
   * A bottom sheet also closes on a swipe down from its handle or header.
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Custom class name for the sheet content */
  className?: string;
  /** Content to display in the sheet */
  children?: React.ReactNode;
  /**
   * Actions (Save) and anything else that must stay in view, pinned under the scrolling body. Never
   * end the body with an action row (`noddi/overlay-actions-in-footer`). A body component that owns
   * its actions renders them through `NavioOverlayActions`, which lands here too.
   */
  footer?: React.ReactNode;
  /** Text used for the close button's accessible label */
  closeText?: string;
  /** Whether the close button should be visible */
  showCloseButton?: boolean;
  /** Mount the body after the opening animation, keeping expensive content off its critical path. */
  deferContentUntilOpen?: boolean;
  /** Whether to show the loading state */
  isLoading?: boolean;
  /** Custom text for the loading state */
  loadingText?: string;
  /** Whether to show the success state */
  isSuccess?: boolean;
  /** Title to show in the default success state */
  successTitle?: string;
  /** Description to show in the default success state */
  successDescription?: string;
  /** Override content for the success state */
  successContent?: React.ReactNode;
  /** Override the icon used in the default success state */
  successIconName?: IconProps['name'];
}

type NoddiSheetProps = BaseNoddiSheetProps &
  (
    | {
        /** The trigger element that opens the sheet in uncontrolled mode */
        trigger: React.ReactNode;
        open?: never;
        onOpenChange?: never;
      }
    | {
        /** Controlled mode: whether the sheet is open */
        open: boolean;
        /** Controlled mode: callback when sheet open state changes */
        onOpenChange: (open: boolean) => void;
        trigger?: never;
      }
  );

export const NoddiSheet = ({
  title,
  description,
  side = 'right',
  className,
  trigger,
  open,
  onOpenChange,
  children,
  footer,
  closeText = 'Close',
  showCloseButton = true,
  deferContentUntilOpen = false,
  isLoading = false,
  loadingText,
  isSuccess = false,
  successTitle = 'Success!',
  successDescription = 'Your action has been completed successfully.',
  successContent,
  successIconName = 'CheckCircle'
}: NoddiSheetProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);

  const isControlled = open !== undefined;
  const sheetOpen = isControlled ? open : internalOpen;
  const setSheetOpen = isControlled ? onOpenChange : setInternalOpen;
  const isBottom = side === 'bottom';
  const swipeHandlers = useSwipeToClose({ enabled: isBottom, onClose: () => setSheetOpen(false) });
  const floatingClassNames =
    side !== 'bottom'
      ? cn(
          'overflow-hidden',
          'border',
          'border-border/40',
          'md:rounded-3xl',
          'shadow-2xl',
          'sm:h-[calc(100vh-2.5rem)]',
          'sm:max-h-[calc(100vh-2.5rem)]',
          (side === 'left' || side === 'right') && 'sm:w-120 sm:max-w-3xl',
          side === 'right' && 'sm:my-5 sm:mr-5',
          side === 'left' && 'sm:my-5 sm:ml-5',
          side === 'top' && 'sm:mx-5 sm:mt-5'
        )
      : undefined;

  const showFooter = footer && !isLoading && !isSuccess;

  const defaultContentClassName = cn(
    // `-mt-1 pt-1`: the scroll container clips focus rings at its padding box, and the top edge
    // is the only one without padding of its own.
    'minimal-scrollbar-hover -mt-1 flex-1 overflow-auto px-4 pt-1 pb-4',
    showFooter && 'pb-24',
    !(title || description) && 'mt-2'
  );

  const renderLoadingState = () => (
    <div className='flex min-h-60 flex-1 items-center justify-center px-4 pb-4'>
      <div className='flex flex-col items-center gap-2'>
        <NoddiCircularLoader loadingText={loadingText ?? 'Loading'} />
      </div>
    </div>
  );

  const renderSuccessState = () => (
    <div className='flex min-h-60 flex-1 flex-col items-center justify-center px-4 pb-4 text-center'>
      <div className='flex flex-col items-center gap-4'>
        <div className='flex size-20 animate-in items-center justify-center rounded-full bg-slate-50 shadow-inner transition-all duration-500 fade-in-0 zoom-in-95'>
          <NoddiIcon name={successIconName} size='xLarge' />
        </div>
        {/* eslint-disable-next-line noddi/enforce-spacing-scale -- not migrated yet: legacy space-y-2 stack, equals flex flex-col gap-2 */}
        <div className='space-y-2'>
          <p className='text-lg font-semibold text-foreground'>{successTitle}</p>
          <p className='text-sm text-muted-foreground'>{successDescription}</p>
        </div>
      </div>
      {successContent}
    </div>
  );

  const bodyContent = (() => {
    if (isLoading) {
      return renderLoadingState();
    }

    if (isSuccess) {
      return renderSuccessState();
    }

    return (
      <div className={defaultContentClassName}>
        <OverlayActionsSlot value={actionsSlot}>{children}</OverlayActionsSlot>
      </div>
    );
  })();

  return (
    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
      {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
      <SheetContent
        ref={contentRef}
        onOpenAutoFocus={onOverlayOpenAutoFocus}
        side={side}
        closeText={closeText}
        showCloseButton={showCloseButton}
        className={cn('focus:outline-hidden', floatingClassNames, className)}
        {...swipeHandlers}
      >
        {isBottom && (
          // Absolute, so the handle leaves the header and close button where they were.
          <div
            data-slot='sheet-drag-handle'
            aria-hidden
            className='absolute top-0 left-1/2 flex h-4 w-16 -translate-x-1/2 touch-none items-center justify-center'
          >
            <div className='h-1 w-10 rounded-full bg-muted-foreground/30' />
          </div>
        )}
        {/* `touch-none`: a drag on the header moves the sheet instead of panning the page. */}
        <SheetHeader className={cn(isBottom && 'touch-none')}>
          <SheetTitle>{title || <VisuallyHiddenPrimitive.Root>Dialog</VisuallyHiddenPrimitive.Root>}</SheetTitle>
          <SheetDescription>
            {description || <VisuallyHiddenPrimitive.Root>No description</VisuallyHiddenPrimitive.Root>}
          </SheetDescription>
        </SheetHeader>
        {deferContentUntilOpen ? (
          <DeferredSheetBody contentRef={contentRef} open={sheetOpen} fallback={renderLoadingState()}>
            {bodyContent}
          </DeferredSheetBody>
        ) : (
          bodyContent
        )}

        <SheetFooter ref={setActionsSlot} className='empty:hidden'>
          {showFooter && footer}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

function DeferredSheetBody({
  contentRef,
  open,
  fallback,
  children
}: {
  contentRef: React.RefObject<HTMLDivElement | null>;
  open: boolean;
  fallback: React.ReactNode;
  children: React.ReactNode;
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    const controller = new AbortController();
    // Read only the shell's animations: a loading spinner in the body never finishes.
    // Without animations, the next microtask releases the body; canceled animations also settle.
    const animations = contentRef.current?.getAnimations() ?? [];
    void Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
      if (!controller.signal.aborted) {
        setReady(true);
      }
      return undefined;
    });
    return () => {
      controller.abort();
    };
  }, [contentRef, open]);

  return ready ? children : fallback;
}
